import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAvailableIDPGroupsQuery } from "@/api/queries/admin/UserGroupQueries";
import { useImportGroupsMutation } from "@/api/mutations/admin/UserGroupMutations";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Download, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

interface ImportGroupsDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface IDPGroup {
    id: string;
    displayName: string;
    description?: string;
    mail?: string;
    mailEnabled: boolean;
    securityEnabled: boolean;
    groupTypes: string[];
    createdDateTime: string;
    source?: string;
}

const ImportGroupsDialog = ({ open, onOpenChange }: ImportGroupsDialogProps) => {
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState("");

    const { data: availableGroups, isLoading: loadingGroups, refetch: refetchGroups } = useAvailableIDPGroupsQuery(open);
    const importMutation = useImportGroupsMutation(onOpenChange, () => {}, () => {});

    // Filter groups based on search term
    const filteredGroups = useMemo(() => {
        if (!availableGroups) return [];
        if (!searchTerm) return availableGroups;
        
        const term = searchTerm.toLowerCase();
        return availableGroups.filter((group: IDPGroup) => 
            group.displayName.toLowerCase().includes(term) ||
            group.description?.toLowerCase().includes(term)
        );
    }, [availableGroups, searchTerm]);

    const handleGroupToggle = (groupId: string) => {
        setSelectedGroupIds(prev => 
            prev.includes(groupId) 
                ? prev.filter(id => id !== groupId)
                : [...prev, groupId]
        );
    };

    const handleSelectAll = () => {
        if (selectedGroupIds.length === filteredGroups.length && filteredGroups.length > 0) {
            setSelectedGroupIds([]);
        } else {
            setSelectedGroupIds(filteredGroups.map((g: IDPGroup) => g.id));
        }
    };

    const handleImport = () => {
        importMutation.mutate({
            groupIds: selectedGroupIds,
            assignUsers: true
        });
    };

    const handleClose = () => {
        if (!importMutation.isPending) {
            setSelectedGroupIds([]);
            setSearchTerm("");
            onOpenChange(false);
        }
    };

    const getGroupTypes = (group: IDPGroup): string[] => {
        const types: string[] = [];
        
        // Check for M365/Unified groups
        if (group.groupTypes?.includes("Unified")) {
            types.push("M365");
        }
        
        // Check for security groups (independent of M365)
        if (group.securityEnabled && !group.groupTypes?.includes("Unified")) {
            types.push("Security");
        }
        
        // Check for mail-enabled (independent of M365)
        if (group.mailEnabled && !group.groupTypes?.includes("Unified")) {
            types.push("Mail");
        }
        
        // For Google Groups
        if (group.source?.includes("Google")) {
            types.push("Google Group");
        }
        
        return types;
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="!max-w-[1280px] w-[98vw] h-[85vh] flex flex-col p-0">
                {/* Header */}
                <div className="px-6 pt-6 pb-4 border-b">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-semibold">
                            Import Groups
                        </DialogTitle>
                        <DialogDescription className="mt-1">
                            Select groups from your identity provider to import into the system
                        </DialogDescription>
                    </DialogHeader>

                    {/* Search and Selection Bar */}
                    <div className="flex items-center gap-4 mt-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search groups..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => refetchGroups()}
                            disabled={loadingGroups}
                            title="Refresh groups"
                        >
                            <RefreshCw className={`h-4 w-4 ${loadingGroups ? 'animate-spin' : ''}`} />
                        </Button>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                                {selectedGroupIds.length} of {filteredGroups.length} selected
                            </span>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="flex-1 overflow-hidden px-6">
                    {loadingGroups ? (
                        <div className="flex flex-col items-center justify-center h-full gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-primary" />
                            <p className="text-sm text-muted-foreground">Loading groups...</p>
                        </div>
                    ) : filteredGroups.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
                            <div className="rounded-full bg-muted p-6">
                                <Search className="h-12 w-12 text-muted-foreground" />
                            </div>
                            <div>
                                <p className="font-medium text-lg">No groups found</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {searchTerm ? "Try adjusting your search criteria" : "No groups available from your identity provider"}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="border rounded-lg overflow-hidden h-full flex flex-col">
                            <Table>
                                <TableHeader className="bg-muted/50 sticky top-0 z-10">
                                    <TableRow>
                                        <TableHead className="w-[50px]">
                                            <Checkbox
                                                checked={selectedGroupIds.length === filteredGroups.length && filteredGroups.length > 0}
                                                onCheckedChange={handleSelectAll}
                                            />
                                        </TableHead>
                                        <TableHead className="font-semibold">Group Name</TableHead>
                                        <TableHead className="font-semibold">Description</TableHead>
                                        <TableHead className="w-[120px] font-semibold">Source</TableHead>
                                        <TableHead className="w-[200px] font-semibold">Type</TableHead>
                                        <TableHead className="w-[280px] font-semibold text-xs">Object ID</TableHead>
                                    </TableRow>
                                </TableHeader>
                            </Table>
                            <div className="flex-1 overflow-auto">
                                <Table>
                                    <TableBody>
                                        {filteredGroups.map((group: IDPGroup) => {
                                            const types = getGroupTypes(group);
                                            const isSelected = selectedGroupIds.includes(group.id);
                                            
                                            return (
                                                <TableRow 
                                                    key={group.id}
                                                    className={`cursor-pointer transition-colors ${
                                                        isSelected ? 'bg-accent/50' : 'hover:bg-accent/30'
                                                    }`}
                                                    onClick={() => handleGroupToggle(group.id)}
                                                >
                                                    <TableCell className="w-[50px]">
                                                        <Checkbox
                                                            checked={isSelected}
                                                            onCheckedChange={() => handleGroupToggle(group.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="font-medium">
                                                        {group.displayName}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm text-muted-foreground line-clamp-2">
                                                            {group.description || "No description"}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="w-[120px]">
                                                        <span className="text-sm">
                                                            {group.source?.replace(" Graph", "").replace("Workspace", "") || "Unknown"}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="w-[200px]">
                                                        <div className="flex flex-wrap gap-1">
                                                            {types.length > 0 ? (
                                                                types.map((type) => (
                                                                    <Badge 
                                                                        key={type} 
                                                                        variant="secondary" 
                                                                        className="text-xs px-2 py-0"
                                                                    >
                                                                        {type}
                                                                    </Badge>
                                                                ))
                                                            ) : (
                                                                <span className="text-sm text-muted-foreground">-</span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="w-[280px]">
                                                        <code className="text-xs text-muted-foreground font-mono bg-muted px-2 py-1 rounded">
                                                            {group.id}
                                                        </code>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t bg-muted/30">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                            Users will be automatically assigned to groups based on their email addresses
                        </p>
                        <Button 
                            onClick={handleImport}
                            disabled={selectedGroupIds.length === 0 || importMutation.isPending}
                        >
                            {importMutation.isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Importing...
                                </>
                            ) : (
                                <>
                                    <Download className="mr-2 h-4 w-4" />
                                    Import {selectedGroupIds.length} {selectedGroupIds.length === 1 ? 'Group' : 'Groups'}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ImportGroupsDialog;